import styled from 'styled-components';
import { Box, Text } from '@rsos/components/capstone/base';
import type { LayerSelectionResult, LayerSelectionMenuProps } from './types';

const MenuContainer = styled(Box).attrs({
  position: 'absolute',
  backgroundColor: 'background',
  borderRadius: '5px',
  p: 3,
  zIndex: 1000,
  minWidth: '200px',
  maxWidth: '300px',
})``;

const Header = styled(Text).attrs({
  size: 'normal',
  fontWeight: 'semibold',
  mb: 2,
  pb: 2,
  backgroundColor: 'background',
  color: 'primaryText',
})`
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const LayerList = styled(Box).attrs({
  maxHeight: '300px',
  overflowY: 'auto',
})`
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.foreground};
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border};
    border-radius: 3px;
  }
`;

const LayerItem = styled(Box).attrs({
  p: 2,
  mb: 1,
  borderRadius: '4px',
  cursor: 'pointer',
})`
  transition: all 0.2s ease;
  &:hover {
    background-color: ${({ theme }) => theme.colors.background};
    margin-left: 2px;
  }
  &:active {
    background-color: ${({ theme }) => theme.colors.border};
  }
`;

const LayerName = styled(Text).attrs({
  size: 'small',
  color: 'primaryText',
})`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/**
 * Displays a menu of available layers for selection at a clicked point
 * @param {Object} props
 * @param {Array} props.results - Array of identify results
 * @param {Object} props.screenPoint - Screen coordinates where the menu should appear
 * @param {Function} props.onLayerSelect - Callback when a layer is selected
 */
const LayerSelectionMenu: React.FC<LayerSelectionMenuProps> = ({
  results,
  screenPoint,
  onLayerSelect,
}) => {
  if (!results?.length || !screenPoint) return null;

  // Calculate position to ensure menu stays within viewport
  const menuStyle: React.CSSProperties = {
    left: screenPoint.x,
    top: screenPoint.y,
    transform: 'translate(-50%, 10px)', // Center horizontally and add some spacing from click point
  };

  return (
    <MenuContainer style={menuStyle}>
      <Header>Select Layer</Header>
      <LayerList>
        {results.map((result: LayerSelectionResult, index: number) => (
          <LayerItem
            key={`${result.layerId}-${index}`}
            onClick={() => onLayerSelect(result)}
            title={result.layerName}
          >
            <LayerName>{result.layerName}</LayerName>
          </LayerItem>
        ))}
      </LayerList>
    </MenuContainer>
  );
};

export default LayerSelectionMenu;
